<script setup lang="ts" generic="T extends Record<string, unknown>">
import { Icon } from '@iconify/vue';
/**
 * UiTable - Design System Table Component
 *
 * A flexible table component with sorting, selection, and loading states.
 * Matches the styling of the live platform doc-search-table.
 *
 * @example
 * <UiTable
 *   :columns="columns"
 *   :data="users"
 *   :loading="isLoading"
 *   selectable
 *   @sort="handleSort"
 *   @selection-change="handleSelection"
 * />
 */
import { computed, ref, watch } from 'vue';

export interface TableColumn<T> {
   /** Unique key for the column (should match data property) */
   key: string;
   /** Display label for the column header */
   label: string;
   /** Width of the column (CSS value) */
   width?: string;
   /** Whether this column is sortable */
   sortable?: boolean;
   /** Custom render function for cell content */
   render?: (value: unknown, row: T, index: number) => string;
   /** Alignment */
   align?: 'left' | 'center' | 'right';
}

export interface UiTableProps<T> {
   /** Column definitions */
   columns: TableColumn<T>[];
   /** Table data */
   data: T[];
   /** Unique key field in data items */
   rowKey?: keyof T;
   /** Whether rows are selectable */
   selectable?: boolean;
   /** Currently selected row keys */
   selectedKeys?: (string | number)[];
   /** Loading state */
   loading?: boolean;
   /** Number of skeleton rows to show when loading */
   skeletonRows?: number;
   /** Current sort field */
   sortField?: string;
   /** Current sort direction */
   sortDirection?: 'ASC' | 'DESC';
   /** Empty state message */
   emptyMessage?: string;
   /** Whether to show striped rows */
   striped?: boolean;
   /** Whether to show hover effect on rows */
   hoverable?: boolean;
   /** Make header sticky */
   stickyHeader?: boolean;
}

const props = withDefaults(defineProps<UiTableProps<T>>(), {
   rowKey: 'id' as keyof T,
   selectable: false,
   selectedKeys: () => [],
   loading: false,
   skeletonRows: 5,
   emptyMessage: 'No data available',
   striped: true,
   hoverable: true,
   stickyHeader: true,
});

const emit = defineEmits<{
   sort: [field: string, direction: 'ASC' | 'DESC'];
   selectionChange: [keys: (string | number)[]];
   rowClick: [row: T, index: number];
}>();

// Internal selection state
const selectedSet = ref<Set<string | number>>(new Set(props.selectedKeys));

// Sync with external selectedKeys prop
watch(() => props.selectedKeys, (newKeys) => {
   selectedSet.value = new Set(newKeys);
}, { immediate: true });

const isAllSelected = computed(() => {
   if (props.data.length === 0)
      return false;
   return props.data.every(row => selectedSet.value.has(row[props.rowKey as keyof T] as string | number));
});

const isSomeSelected = computed(() => {
   if (props.data.length === 0)
      return false;
   const selectedCount = props.data.filter(row => selectedSet.value.has(row[props.rowKey as keyof T] as string | number)).length;
   return selectedCount > 0 && selectedCount < props.data.length;
});

function toggleSelectAll() {
   if (isAllSelected.value) {
      // Deselect all
      props.data.forEach(row => selectedSet.value.delete(row[props.rowKey as keyof T] as string | number));
   }
   else {
      // Select all
      props.data.forEach(row => selectedSet.value.add(row[props.rowKey as keyof T] as string | number));
   }
   emit('selectionChange', Array.from(selectedSet.value));
}

function toggleRowSelection(row: T) {
   const key = row[props.rowKey as keyof T] as string | number;
   if (selectedSet.value.has(key)) {
      selectedSet.value.delete(key);
   }
   else {
      selectedSet.value.add(key);
   }
   emit('selectionChange', Array.from(selectedSet.value));
}

function isRowSelected(row: T): boolean {
   return selectedSet.value.has(row[props.rowKey as keyof T] as string | number);
}

function handleSort(column: TableColumn<T>) {
   if (!column.sortable)
      return;

   let newDirection: 'ASC' | 'DESC' = 'ASC';
   if (props.sortField === column.key) {
      newDirection = props.sortDirection === 'ASC' ? 'DESC' : 'ASC';
   }
   emit('sort', column.key, newDirection);
}

function handleRowClick(row: T, index: number) {
   emit('rowClick', row, index);
}

function getCellValue(row: T, column: TableColumn<T>, index: number): string {
   const value = row[column.key as keyof T];
   if (column.render) {
      return column.render(value, row, index);
   }
   if (value == null || value === '')
      return '';
   return String(value);
}

const headerClasses = computed(() => {
   const classes = ['bg-neutral text-base font-bold text-black/80'];
   if (props.stickyHeader) {
      classes.push('sticky top-0 z-10');
   }
   return classes.join(' ');
});

function getAlignClass(align?: 'left' | 'center' | 'right') {
   switch (align) {
      case 'center': return 'text-center';
      case 'right': return 'text-right';
      default: return 'text-left';
   }
}
</script>

<template>
   <div
      class="bg-white flex flex-col overflow-hidden rounded-2xl border border-black/10"
   >
      <div class="overflow-x-auto overflow-y-auto">
         <table class="table w-full font-medium">
            <colgroup>
               <col v-if="selectable" style="width: 54px" />
               <col
                  v-for="column in columns"
                  :key="column.key"
                  :style="column.width ? { width: column.width } : undefined"
               />
            </colgroup>

            <thead :class="headerClasses">
               <tr class="border-b-2 border-neutral-dark/75">
                  <!-- Checkbox column header -->
                  <th v-if="selectable" scope="col" class="px-3 py-4">
                     <input
                        type="checkbox"
                        class="ui-table-checkbox"
                        :checked="isAllSelected"
                        :indeterminate="isSomeSelected"
                        :disabled="data.length === 0 || loading"
                        @change="toggleSelectAll"
                     />
                  </th>

                  <!-- Data column headers -->
                  <th
                     v-for="column in columns"
                     :key="column.key"
                     scope="col"
                     class="px-3 py-3 font-bold tracking-wide whitespace-nowrap"
                     :class="[
                        getAlignClass(column.align),
                        column.sortable ? 'cursor-pointer hover:bg-neutral-dark/20 transition-colors select-none' : '',
                     ]"
                     @click="handleSort(column)"
                  >
                     <div class="flex items-center gap-2" :class="column.align === 'right' ? 'justify-end' : column.align === 'center' ? 'justify-center' : ''">
                        <span class="text-sm">{{ column.label }}</span>

                        <!-- Sort icon -->
                        <div
                           v-if="column.sortable"
                           class="p-1 rounded transition-all duration-150"
                           :class="sortField === column.key
                              ? 'bg-[#CBDCDC] text-[#073336]'
                              : 'text-black/30'"
                        >
                           <Icon
                              v-if="sortField !== column.key"
                              icon="mdi:sort"
                              class="size-4"
                           />
                           <Icon
                              v-else-if="sortDirection === 'ASC'"
                              icon="ph:sort-ascending"
                              class="size-4"
                           />
                           <Icon
                              v-else
                              icon="ph:sort-descending"
                              class="size-4"
                           />
                        </div>
                     </div>
                  </th>
               </tr>
            </thead>

            <tbody>
               <!-- Loading skeleton -->
               <template v-if="loading">
                  <tr
                     v-for="i in skeletonRows"
                     :key="`skeleton-${i}`"
                     class="border-none bg-white"
                  >
                     <td v-if="selectable" class="px-3 py-4">
                        <div class="h-5 w-5 rounded bg-neutral-dark/45 animate-pulse" />
                     </td>
                     <td
                        v-for="column in columns"
                        :key="`skeleton-${i}-${column.key}`"
                        class="px-3 py-4"
                     >
                        <div class="h-6 rounded bg-neutral-dark/45 animate-pulse w-3/4" />
                     </td>
                  </tr>
               </template>

               <!-- Empty state -->
               <tr v-else-if="data.length === 0">
                  <td
                     :colspan="selectable ? columns.length + 1 : columns.length"
                     class="px-6 py-10 text-center text-black/70"
                  >
                     {{ emptyMessage }}
                  </td>
               </tr>

               <!-- Data rows -->
               <tr
                  v-for="(row, index) in data"
                  v-else
                  :key="String(row[rowKey as keyof typeof row])"
                  class="border-none text-base text-black/80 transition-all duration-200"
                  :class="[
                     striped && index % 2 === 1 ? 'bg-gray-50/70' : 'bg-white',
                     hoverable ? 'hover:bg-neutral-dark/30' : '',
                  ]"
                  @click="handleRowClick(row, index)"
               >
                  <!-- Checkbox cell -->
                  <td v-if="selectable" class="px-3 py-4 align-top">
                     <input
                        type="checkbox"
                        class="ui-table-checkbox"
                        :checked="isRowSelected(row)"
                        @click.stop
                        @change="toggleRowSelection(row)"
                     />
                  </td>

                  <!-- Data cells -->
                  <td
                     v-for="column in columns"
                     :key="column.key"
                     class="px-3 py-4 align-top"
                     :class="getAlignClass(column.align)"
                  >
                     <slot :name="`cell-${column.key}`" :value="row[column.key as keyof T]" :row="row" :index="index">
                        {{ getCellValue(row, column, index) }}
                     </slot>
                  </td>
               </tr>
            </tbody>
         </table>
      </div>

      <!-- Footer slot for pagination etc -->
      <div v-if="$slots.footer" class="border-t border-neutral-dark/75 px-4 py-3">
         <slot name="footer" />
      </div>
   </div>
</template>

<style scoped>
.ui-table-checkbox {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 2px solid rgba(0, 0, 0, 0.12);
  background: #ffffff;
  display: inline-grid;
  place-content: center;
  cursor: pointer;
  transition: background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease;
}

.ui-table-checkbox::after {
  content: '';
  width: 6px;
  height: 12px;
  border-right: 2px solid #111111;
  border-bottom: 2px solid #111111;
  transform: rotate(45deg) translateY(-1px) translateX(-1px);
  opacity: 0;
  transition: opacity 120ms ease;
}

.ui-table-checkbox:checked {
  background: #CCDBDC;
  border-color: transparent;
}

.ui-table-checkbox:checked::after {
  opacity: 1;
}

.ui-table-checkbox:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.12);
}

.ui-table-checkbox:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.ui-table-checkbox:indeterminate {
  background: #CCDBDC;
  border-color: transparent;
}

.ui-table-checkbox:indeterminate::after {
  width: 10px;
  height: 2px;
  border: none;
  background: #111111;
  transform: none;
  opacity: 1;
}
</style>
